import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, CheckCircle2, Send } from 'lucide-react';

import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatIST } from '@/lib/utils';
import {
  AppHeader, EmptyState, ScreenLoader, Spinner, StatusPill,
} from '@/components/phone/primitives';
import { Colors } from '@/components/phone/theme';

/**
 * Plates that are still owed.
 *
 * A plate is made outside, so the two facts nobody on the floor can scan — it
 * went out, it came back — have to be typed by someone, and the supervisor is
 * who is standing there when the van arrives.
 *
 * One row, one button, and the button is whatever comes next: a pending plate
 * says "Ordered", an ordered one says "Received". A plate that has come back is
 * off the list entirely — it is not work any more.
 *
 * The desktop Plate Tracking page is a four-tab table that also carries Received
 * and All; this is the phone's list, which is only ever what is still owed.
 *
 * Mirrors `mobile-frontend/app/(tabs)/plates.tsx`.
 */
export default function SupervisorPlatesPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [marking, setMarking] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [board, unread] = await Promise.all([
        api.get('/plates/tracking?status=open&limit=200'),
        api.get('/notifications/unread-count'),
      ]);
      setRows(board.data.plates || []);
      setCounts(board.data.counts || {});
      setUnreadCount(unread.data.count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the plate board');
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const advance = async (row) => {
    const action = row.plate_status === 'ordered' ? 'received' : 'ordered';
    setMarking(row.line_item_id);
    try {
      await api.post(`/plates/orders/${row.order_uuid}/items/${row.line_item_id}/mark-${action}`, {});
      // it drops off the list the moment it is back, so the list is only ever
      // what is still owed
      toast.success(action === 'received'
        ? `${row.item_name || 'Plate'} received`
        : `${row.item_name || 'Plate'} marked ordered`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the plate');
    }
    setMarking(null);
  };

  return (
    <div className="flex flex-1 flex-col px-5">
      <AppHeader
        greeting={t('nav.plates')}
        subtitle={user?.full_name || ''}
        unreadCount={unreadCount}
        onNotifications={() => navigate('/supervisor/notifications')}
        testID="notifications-btn"
      />

      <div className="flex items-baseline justify-between gap-3">
        <h1 className="text-[17px] font-bold -tracking-[0.3px]" style={{ color: Colors.text.primary }}>
          {t('plates.still_owed')}
        </h1>
        <p className="text-[11px] font-medium" style={{ color: Colors.text.tertiary }}>
          {counts.pending ?? 0} {t('plates.to_order')} · {counts.ordered ?? 0} {t('plates.out')}
        </p>
      </div>

      {loading ? <ScreenLoader /> : rows.length === 0 ? (
        <EmptyState icon={CheckCircle2} message={t('plates.none_waiting')} />
      ) : (
        <ul className="pb-6">
          {rows.map((row) => {
            const out = row.plate_status === 'ordered';
            const busy = marking === row.line_item_id;
            return (
              <li
                key={row.line_item_id}
                data-testid={`plate-${row.line_item_id}`}
                className="mt-3 flex items-center gap-3 rounded-[10px] border px-4 py-3"
                style={{ backgroundColor: Colors.background.primary, borderColor: Colors.border.default }}
              >
                <button
                  type="button"
                  onClick={() => navigate(`/supervisor/order/${row.order_uuid}`)}
                  className="min-w-0 flex-1 text-left"
                >
                  <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
                    {row.item_name || 'Untitled item'}
                  </span>
                  <span className="mt-0.5 block truncate text-[13px] font-medium" style={{ color: Colors.text.secondary }}>
                    {row.firm_name || row.customer_name || 'Unknown'}
                  </span>
                  <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
                    {row.quantity} pcs · {row.order_id}
                    {row.delivery_date ? ` · ${formatIST(row.delivery_date, { compact: true })}` : ''}
                  </span>
                  <span className="mt-2 flex flex-wrap items-center gap-2">
                    <StatusPill
                      label={out ? t('plates.ordered') : t('plates.pending')}
                      tone={out ? 'accent' : 'caution'}
                      dot
                    />
                    {/* the job is standing at Plate Making — the press is waiting */}
                    {row.holding_production && <StatusPill label={t('plates.holding')} tone="critical" />}
                    {row.days_waiting > 0 && (
                      <span
                        className="text-[11px]"
                        style={{
                          color: row.days_waiting > 3 ? Colors.status.error : Colors.text.tertiary,
                          fontWeight: row.days_waiting > 3 ? 700 : 500,
                        }}
                      >
                        {row.days_waiting}d
                      </span>
                    )}
                  </span>
                  {out && !!row.plate_ordered_at && (
                    <span className="mt-1 block truncate text-[10px]" style={{ color: Colors.text.tertiary }}>
                      {t('plates.sent')} {formatIST(row.plate_ordered_at, { compact: true })}
                      {row.plate_ordered_by_name ? ` · ${row.plate_ordered_by_name}` : ''}
                    </span>
                  )}
                </button>

                {/* one button, and it is whatever happens next */}
                <button
                  type="button"
                  data-testid={`plate-advance-${row.line_item_id}`}
                  onClick={() => advance(row)}
                  disabled={busy}
                  className="flex min-w-[104px] shrink-0 items-center justify-center gap-1.5 rounded-[10px] px-3 py-2.5 disabled:opacity-70"
                  style={{ backgroundColor: out ? Colors.status.success : Colors.accent.DEFAULT }}
                >
                  {busy ? <Spinner size="sm" className="!border-white !border-b-transparent" /> : (
                    <>
                      {out
                        ? <Check className="h-3.5 w-3.5" style={{ color: Colors.text.inverse }} />
                        : <Send className="h-3.5 w-3.5" style={{ color: Colors.text.inverse }} />}
                      <span className="text-[12px] font-bold" style={{ color: Colors.text.inverse }}>
                        {out ? t('plates.mark_received') : t('plates.mark_ordered')}
                      </span>
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
